import { Component } from 'react';
import _ from 'underscore';
import type { MouseEvent } from 'react';

import { Action } from '../../../../typings/permissions';
import { updateProjectDirectLicenses } from '../../../api/licenses';
import { createRevisionLicense, getRevisionDeepLicenses, getRevisionDeepTodos } from '../../../api/revisions';
import { errorMessageFromResponse } from '../../../helpers/errors';
import { compactRevision } from '../../../helpers/revisions';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import utils from '../../../utils';
import Button from '../../generic/Button/Button';
import Flex from '../../generic/Flex';
import { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import Text from '../../generic/Text';
import ModalEditLicenseGroups from '../../licenses/ModalEditGroups';
import ModalAddLicense from '../../modals/ModalAddLicense';
import ModalDisplayLicense from '../../modals/ModalDisplayLicense';
import TodoListSection from '../TodoListSection';
import type { ListRevisionDeepLicensesResponse, ListRevisionDeepTodosResponse } from '../../../../typings/api';
import type {
  IAsync,
  IAsyncStatus,
  License,
  Project,
  Revision,
  RevisionLicense,
} from '../../../types';
import type { CustomLicense } from '../../modals/ModalAddLicense';
import type { MultipleRevisionsDisplayMode } from '../../modals/ModalDisplayLicense';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import { ConcludeLicenseButton } from './ConcludeLicenseButton';
import { ProjectConclusionBanner } from './ProjectConclusionBanner';

const NoLicensesAction = ({ onAdd, projectId }: {
  onAdd: (event: MouseEvent) => void;
  projectId: string;
}) => {
  if (!userHasPermissionForProject(Action.Edit, projectId)) {
    return null;
  }
  return (
    <Button type="outline" icon={icons.add} onClick={onAdd}>Add License</Button>
  );
};

const DirectLicensesAction = ({ onEdit, projectId }: {
  onAdd: (event: MouseEvent) => void;
  onEdit: (event: MouseEvent) => void;
  projectId: string;
}) => {
  if (!userHasPermissionForProject(Action.Edit, projectId)) {
    return null;
  }
  return (
    <Button type="outline" icon={icons.edit} onClick={onEdit} size="micro">Edit</Button>
  );
};

type Props = {
  params: RevisionRouteParams;
  revision: IAsync<Revision>;
  project: IAsync<Project>;
  show_ignored_licenses?: boolean;
};

type State = {
  focusLicense: string | null;
  isEditingLicenses: boolean;
  show_add_license_modal?: boolean;
  licenses: ListRevisionDeepLicensesResponse;
  todos: ListRevisionDeepTodosResponse | null;
  selected_obligation?: string | null;
  show_ignored_licenses?: boolean;
} & IAsyncStatus<Error | string | null>;

export default class ProjectBrowseLicensesPage extends Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = {
      focusLicense: null,
      isEditingLicenses: false,
      show_add_license_modal: false,
      loading: false,
      licenses: [],
      todos: null,
      selected_obligation: null,
      show_ignored_licenses: false,
      error: null,
    };
  }

  componentDidMount(): void {
    this.loadDeepLicenses();
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.params.revision_id !== prevProps.params.revision_id) {
      this.loadDeepLicenses();
    }
  }

  get focusLicenseMatches(): string[] {
    const { licenses, focusLicense } = this.state;
    if (!focusLicense) {
      return [];
    }
    const isFocusLicense = ({ licenseId }: { licenseId: string }) => licenseId === focusLicense;
    return licenses.find(isFocusLicense)?.matches ?? [];
  }

  loadDeepLicenses = async () => {
    this.setState({ loading: true, error: null });
    const locator = `${this.props.params.project_id}$${this.props.params.revision_id}`;
    try {
      const [licenses, todos] = await Promise.all([
        getRevisionDeepLicenses(locator),
        getRevisionDeepTodos(locator),
      ]) as [ListRevisionDeepLicensesResponse, ListRevisionDeepTodosResponse];

      this.setState({
        todos,
        licenses: (licenses || []).map(license => Object.assign(license, {
          // Place the current project's current revision first if it's among the matches. Otherwise, sort the revision
          // locators alphabetically.
          matches: (license.matches || []).sort((a, b) => {
            if (a === locator) {
              return -1;
            }
            if (b === locator) {
              return 1;
            }
            return a.localeCompare(b);
          }),
        })),
        loading: false,
      });
    } catch (error) {
      this.setState({ error, loading: false });
    }
  };

  // must return promise
  onAddLicense = (customLicense: CustomLicense) => {
    this.setState({ loading: true, error: null });
    return createRevisionLicense(
      `${this.props.params.project_id}$${this.props.params.revision_id}`,
      customLicense,
    ).then(() => this.loadDeepLicenses())
      .catch(error => this.setState({ error, loading: false }));
  };

  onToggleModal = (stateAttr: keyof State, e: MouseEvent) => {
    e?.stopPropagation();
    this.setState((prevState: State) => ({
      ...prevState,
      [stateAttr]: !prevState[stateAttr],
    }));
  };

  onClickAddLicense = () =>
    this.setState({ show_add_license_modal: true });

  handleEditLicenses = () =>
    this.setState({ isEditingLicenses: true });

  handleCancelEditLicenses = () => {
    this.setState({ isEditingLicenses: false });
  };

  handleSaveEditLicenses = (groups: Array<Array<Partial<RevisionLicense>>>) => {
    const { project } = this.props;
    this.setState({
      isEditingLicenses: false,
      loading: true,
      error: undefined,
    });
    return updateProjectDirectLicenses(project.data.locator, groups)
      .then(this.loadDeepLicenses)
      .catch(error => this.setState({ error: errorMessageFromResponse(error), loading: false }));
  };

  /** How this revision is named in the conclusion dialogs, e.g. "my-app 1a2b3c4". */
  getPackageName = () =>
    `${this.props.project.data!.title!} ${compactRevision(`${this.props.params.project_id}$${this.props.params.revision_id}`)}`;

  onSelectLicense = (license: string) => {
    this.setState({ focusLicense: license });
  };

  getLicensePanelHeaderText = (depth) => {
    switch (depth) {
      case '0':
        return 'Directly in Code';
      case '1':
        if (this.props.project.data.type === 'container') {
          return 'Starting in Dependencies';
        }
        return 'Starting in Direct Dependencies';
      default:
        return `Starting From ${depth}-Level Transitive Dependencies`;
    }
  };

  renderLicensePanel = () => {
    const { licenses } = this.state;

    // group by min-encountered depth, or if no depth available, check if Provided build
    const licenseDepthGroups: Record<number, License[]> = _.groupBy(licenses, 'depth');

    return (
      <div className="panel">
        <div className="panel-header padded padded-small">
          <Flex justify="space-between">
            <Text.H3>
              {licenses.length} Licenses Found
            </Text.H3>
            {!licenseDepthGroups['0'] && (
              <NoLicensesAction
                onAdd={this.onClickAddLicense}
                onEdit={this.handleEditLicenses}
                projectId={this.props.project.data.locator}
              />
            )}
          </Flex>
        </div>
        <ul className="panel-list">
          {_.map(licenseDepthGroups, (group: License[], depth: string) => {
            const renderedGroup = [
              <li key="header" className="header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{this.getLicensePanelHeaderText(depth)}</span>
                {depth === '0' && (
                  <DirectLicensesAction
                    onAdd={this.onClickAddLicense}
                    onEdit={this.handleEditLicenses}
                    projectId={this.props.project.data.locator}
                  />
                )}
              </li>,
            ];
            let numIgnored = 0;
            _.each(_.sortBy(group, l => l.licenseId.toLocaleLowerCase()), (license: License) => {
              let isIgnored = false;
              let revLicense : RevisionLicense;
              if (depth === '0') {
                revLicense = _.find(
                  this.props.revision.data.licenses,
                  (l: RevisionLicense) => l.licenseId === license.licenseId,
                );
                isIgnored = revLicense && revLicense.ignored;
                if (isIgnored && !this.state.show_ignored_licenses) {
                  numIgnored++;
                  return;
                }
              }
              renderedGroup.push(
                <li
                  key={license.licenseId}
                  onClick={() => this.onSelectLicense(license.licenseId)}
                  style={depth === '0' ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } : undefined}
                >
                  <span>{license.name}</span>
                  {depth === '0' && userHasPermissionForProject(Action.Edit, this.props.project.data!.locator) && (
                    <ConcludeLicenseButton
                      licenseId={license.licenseId}
                      revisionLocator={`${this.props.params.project_id}$${this.props.params.revision_id}`}
                      projectLocator={this.props.project.data!.locator!}
                      packageName={this.getPackageName()}
                      organizationId={this.props.project.data!.organizationId!}
                    />
                  )}
                </li>,
              );
            });
            if (numIgnored > 0 && !this.props.show_ignored_licenses) {
              renderedGroup.push(
                <li
                  style={{ padding: '5px 15px' }}
                  onClick={this.onToggleModal.bind(null, 'show_ignored_licenses')}
                  key="ignored-license-num">
                  <i className="subtitle">
                    <b>({numIgnored} Ignored Licenses...)</b>
                  </i>
                </li>,
              );
            }
            return renderedGroup;
          })}
          {(this.props.project.data.organization?.access_level !== 'premium' && utils.isProjectInCurrentOrg(this.props.project.data?.organizationId)) && (
            <div className="panel-header padded padded-small text-center">
              <i>
                Browsing access is limited to a depth of{' '}
                <b>{utils.getDepthByAccessLevel(this.props.project.data.organization?.access_level)}</b>. Upgrade to a
                paid plan to increase the access depth.
              </i>
            </div>
          )}
        </ul>
      </div>
    );
  };

  render() {
    if (this.state.loading || this.props.revision.status.loading) {
      return (
        <div className="margin-top">
          <Loader />
        </div>
      );
    }
    const hasTodos = this.state.todos && this.state.todos.MUST && !_.isEmpty(this.state.todos.MUST);

    const { focusLicense } = this.state;
    const focusLicenseMatchIds: string[] | null = focusLicense
      ? this.focusLicenseMatches // Store in the render so that we only calculate it once.
      : null;
    let licenseDisplayMode: MultipleRevisionsDisplayMode | null = null;
    if (focusLicense) {
      licenseDisplayMode = {
        mode: 'multiple',
        licenseId: focusLicense,
        matchRevisionIds: focusLicenseMatchIds !== null
          ? focusLicenseMatchIds
          : [],
      };
    }

    return (
      <div className="container container-responsive">
        <div className="margin-top">
          {this.state.error && (
          <div className="margin-top">
            <div className="alert alert-error">{this.state.error.toString()}</div>
          </div>
          )}
          <div className="animated fadeInDown quick">
            <div className="row">
              <div className={!hasTodos ? 'col-md-12' : 'col-md-7'}>
                <ProjectConclusionBanner
                  revisionLocator={`${this.props.params.project_id}$${this.props.params.revision_id}`}
                  projectLocator={this.props.project.data!.locator!}
                  packageName={this.getPackageName()}
                  organizationId={this.props.project.data!.organizationId!}
                  allLicenseIds={this.state.licenses.map(l => l.licenseId)}
                  canEditLicenses={userHasPermissionForProject(Action.Edit, this.props.project.data!.locator)}
                />
                {this.renderLicensePanel()}
              </div>
              {hasTodos && (
              <div className="col-md-5">
                <div className="todo-main">
                  <TodoListSection
                    params={this.props.params}
                    obligationType="MUST"
                    obligations={this.state.todos.MUST}
                    selectedItem={this.state.selected_obligation}
                    onClickItem={(obg) =>
                      this.setState({
                        selected_obligation: obg === this.state.selected_obligation ? null : obg,
                      })}
                    revisionId={this.props.revision.data.locator}
                    onSelectLicense={this.onSelectLicense}
                    defaultExpanded
                    />
                </div>
              </div>
              )}
            </div>
          </div>
          <br />
          <ModalDisplayLicense displayMode={licenseDisplayMode} onHide={() => this.setState({ focusLicense: null })} />

          <ModalAddLicense
            show={this.state.show_add_license_modal}
            onHide={() => {
              this.setState({ show_add_license_modal: false });
            }}
            previousLicenseCount={this.state.licenses.length}
            onAddLicense={this.onAddLicense}
            />
          {/* TODO: this will be removed, replaced with <ModalLicenseMatch /> */}
          <ModalEditLicenseGroups
            show={this.state.isEditingLicenses}
            onCancel={this.handleCancelEditLicenses}
            onSave={this.handleSaveEditLicenses}
            revision={this.props.revision?.data}
            />
        </div>
      </div>
    );
  }
}
